import { useState } from 'react'
import { ChevronDown, Info, Clock, AlertTriangle } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_BODY } from '@/config/theme'
import type { FeeEstimate } from '@/types'
import { isCrossChainFeeDisplayEnabled } from '@/config/flags'
import { computeDexDisplayTotals, resolveDexFeeDisplay, trimAmt, usdRendersAsZero, usdIsMissing, USD_SUBCENT_BOUND } from './dexTotal'

interface FeeSectionProps {
  feeEstimate: FeeEstimate | null
  amount: string
  secondsRemaining: number
  fiatSymbol?: string
  fiatRate?: number | null
  fiatStale?: boolean
  formatFiatAmount?: (usdcAmount: string | number) => string | null
  /**
   * Override for the "Minimum received" line, in dest-token terms. For DEX swaps the dest
   * may not be USDC (e.g. native ETH), so the generic USDC-denominated feeEstimate value is
   * wrong. When provided, this amount + symbol are shown instead. Symbol drives the unit label.
   */
  minReceivedAmount?: string | null
  minReceivedSymbol?: string
  /**
   * DEX-route fee breakdown, in DEST-token terms. When provided (user selected a DEX/swap route),
   * the panel shows these instead of the CCTP feeEstimate — totals, the Parse platform fee, and
   * the minimum received are all denominated in `currency` (the dest token), and the warning badge
   * reflects the swap's all-in cost rather than CCTP fee logic.
   *
   * THREE-VALUED BY CONTRACT (#336 R10) — `null` and `undefined` are NOT interchangeable here:
   *   · `undefined` — no DEX fee surface at all (a CCTP route, or a swap with nothing computed yet).
   *   · `null`      — a DEX route whose fee the parent REFUSED to state (the R3/R7 staleness guard,
   *                   BridgePage.tsx:1619). This card must say 'Fee unavailable' out loud.
   * Producer side is documented on that memo. Do NOT normalise the two together (`dexFee ?? null`,
   * `!dexFee`): before R10 this component could not tell them apart, so the refusal fell through to
   * the neutral 'Fee details' placeholder with no expandable panel — i.e. the card announced "this
   * route has no fees" for a route whose fee had just been judged unstateable. That reads as a zero,
   * which is the exact misreading R1/R2/R6 exist to eliminate.
   */
  dexFee?: {
    totalFee: string
    parseFee: string
    affiliateBps: number
    minimumReceived: string | null
    currency: string
    warningLevel: 'green' | 'orange' | 'red'
    estimatedTimeMin?: number
    /** All-in fee in USD = you'll-pay − receive (fixed fee + route margin + price impact + affiliate). */
    feesTotalUsd?: number
    /** Generic provider/rail fee in USD (e.g. the 1Click rail), itemized separately from the Parse fee. */
    networkFeeUsd?: number
    /** Our actual Parse fee in USD (source-sized). When present, shown instead of the dest-token `parseFee`
     *  — for near_1click the appFee is a SOURCE-token cut, so the dest-sized amount understates it. */
    parseFeeUsd?: number
    /** Tron source-side energy burn (USD) + whether the wallet already has enough energy. */
    tronEnergyFeeUsd?: number
    tronHasEnergy?: boolean
    /** deBridge flat protocol fee — native-token units + USD. Source-side, paid on top of input. */
    fixFeeNative?: string
    /** Symbol of the fee's native token (ETH/POL…); for stable source it differs from the source token. */
    fixFeeSymbol?: string
    fixFeeUsd?: number
    /** True wallet debit (input + opex + fixFee), source token units, excl. gas. */
    youPaySource?: string
    youPayUsd?: number
    sourceCurrency?: string
  } | null
  /**
   * #261 L2 — is this route same-chain? Drives the delivery-time span and NOTHING else.
   *
   * OPTIONAL, and absent means cross-chain ON PURPOSE: cross-chain carries the wider 1–5 min
   * estimate, so a caller that cannot say defaults to the over-estimate rather than the promise
   * it might miss.
   */
  isSameChain?: boolean
}
// #614 — `isSameChain` means ATOMIC same-chain (one wallet tx). A same-chain deposit-flow order (Tron
// TRX↔USDT via 1Click) is passed as false by its caller so it carries the wider 1–5 min estimate.

export function FeeSection({ feeEstimate, amount, secondsRemaining, fiatSymbol = '$', fiatRate, fiatStale, formatFiatAmount, minReceivedAmount, minReceivedSymbol, dexFee, isSameChain }: FeeSectionProps) {
  const c = useThemeColors()
  const [expanded, setExpanded] = useState(false)

  const amountNum = parseFloat(amount || '0')
  const hasFees = (dexFee || feeEstimate) && amountNum > 0

  /**
   * #336 R10 — the DEX REFUSAL state, which `hasFees` above cannot see (both falsy `dexFee` values
   * collapse there). `dexFee === null` is the parent saying "this IS a swap route and I will not state
   * its fee"; `undefined` is "no DEX fee here" (see the prop doc). Only the former earns a spoken
   * label, and only ever on an amount the user has actually entered.
   *
   * SCOPE — the LABEL only, deliberately not `hasFees` itself. With `dexFee` null and `feeEstimate`
   * null the expanded breakdown has no branch to render (:259 falls through `dexFee ? … : feeEstimate
   * ? … : null`), so widening `hasFees` would open an EMPTY styled panel — and every row inside the
   * DEX branch dereferences `dexFee.currency`, so a half-widened version crashes instead. It would
   * also switch on the `>100%` badge and the quote countdown for a quote we are refusing to quote.
   * Label without a panel is the honest shape: it states what is true (no fee figure) and offers
   * nothing further, which is precisely the information available. The chevron stays inert exactly as
   * it already is in the 'Fee details' state, so no affordance changes.
   *
   * CCTP IS UNTOUCHED BY CONSTRUCTION: on a CCTP route the parent passes `undefined`, so this is
   * false and the label keeps today's 'Fee details' during a quote load. A CCTP quote still loading
   * is not a refusal and must not be announced as one.
   */
  const dexFeeRefused = dexFee === null && amountNum > 0

  // CCTP "Total" must equal network fee + Parse platform fee so it reconciles with
  // "Minimum received" (amount − network − platform). The backend's `totalFee` field is
  // platform-only (api.ts maps it to percentageFeeDisplay), so we recompute the all-in total
  // here. The referrer cut is carved FROM the platform fee, not additive — do not add it.
  const displayTotalFee = feeEstimate
    ? Number(
        (parseFloat(feeEstimate.networkFeeUsdc || '0')
          + parseFloat(feeEstimate.baseFee || '0')
          + parseFloat(feeEstimate.circleMaxFeeUsdc || '0')  // Circle pass-through — keeps Total == PAY − RECEIVE
        ).toFixed(6)
      ).toString()
    : '0'

  // Fee warning — CCTP only. `feeLevel` (which mapped dexFee.warningLevel / feeWarning into
  // green|orange|red) was removed with the Elevated/High badge: nothing reads it any more, and the
  // only surviving badge keys directly off feeWarning.level === 'exceeds_amount'.
  // NOTE the asymmetry this leaves: feeWarning is undefined on DEX routes (they carried their level in
  // dexFee.warningLevel), so DEX shows NO badge at all — the intended outcome — while CCTP retains the
  // >100% case. DEX never had a >100% level to begin with; its warningLevel only went as far as 'red'.
  const feeWarning = dexFee ? undefined : feeEstimate?.feeWarning

  // Fiat annotation helper
  const fiatNote = (usdcAmt: string) => {
    if (!formatFiatAmount || fiatRate == null) return null
    const formatted = formatFiatAmount(usdcAmt)
    if (!formatted) return null
    return (
      <span style={{ color: c.mu, fontSize: 12, marginLeft: 4, opacity: fiatStale ? 0.5 : 0.8 }}>
        ({formatted})
      </span>
    )
  }

  // Fiat conversion for the DEX fee line-items. These values are already USD; the branch below
  // historically rendered `${fiatSymbol}${usd.toFixed(2)}`, which only swaps the SYMBOL (e.g. "Rp")
  // onto the raw dollar figure WITHOUT applying the exchange rate — so IDR showed "Rp45.88" for a
  // $45.88 fee instead of the converted ~Rp745,000 (the top-line amounts convert via formatFiatAmount,
  // so the fees looked inconsistent). Route them through formatFiatAmount (usd×rate + Intl currency
  // format, same as the CCTP branch's fiatNote); fall back to plain USD when the rate hasn't loaded.
  const fiatFee = (usd: number): string => formatFiatAmount?.(usd) ?? `$${usd.toFixed(2)}`

  // Delivery time text \u2014 #261 L2. ONE rule by ROUTE TYPE (operator, 2026-08-03): same-chain
  // 1\u20133 min, cross-chain 1\u20135 min. Over-estimate beats under-promise; a swap that lands early is a
  // pleasant surprise, the reverse erodes trust.
  //
  // Fixed on purpose, replacing the old `n\u2013(n+2)` span, which could not express this rule at all:
  // cross-chain was unreachable above 1\u20133 without also moving the lower bound, and the CCTP arm
  // derived a span from `feeEstimate.estimatedTime` while every DEX route \u2014 where `feeEstimate` is
  // structurally absent \u2014 silently fell through to the same 1\u20133. Neither input was a measurement:
  // `dexFee.estimatedTimeMin` is a provider hint (hardcoded 1) and `feeEstimate.estimatedTime` is a
  // hardcoded 60 in `services/api.ts` \u2014 constants, not measurements. This string must agree with the
  // two other surfaces stating the same fact: the bridge row in `utils/steps.ts` and the review
  // card's `reviewDeliveryTime` in BridgePage.
  //
  // ONE KNOWN DISAGREEMENT REMAINS, pre-existing and NOT introduced here: with
  // `VITE_FAILURE_STEP_STATE` off (its prod default), a SAME-CHAIN swap reopened from Activity is
  // narrated by `makeSteps` \u2014 the cross-chain builder \u2014 so that row says "Usually 1\u20135 min" while
  // this card said 1\u20133. `makeSteps` is therefore NOT strictly cross-chain-only while that flag is
  // off. Fixing it belongs to the flag's own rollout, not to a copy change.
  const deliveryTime = isSameChain ? '1\u20133 min' : '1\u20135 min'

  // DEX "Total fees" display reconciliation (issue #50) \u2014 clamp the SHOWN total up to the sum of the
  // SHOWN line items so a same-chain Jupiter swap can't render "Total fees 0" above a nonzero Parse
  // fee. Display-only; see dexTotal.ts. `dexFee` is truthy wherever these are consumed below.
  // #336: when the clamp still leaves nothing truthful to show (`belowDisplayFloor`), BOTH render
  // sites say 'Fee unavailable' rather than "0 <TOKEN>" — the same string the review modal shows,
  // so the card and the last screen before signing cannot disagree.
  /**
   * #261 F8 — the flag, read HERE rather than threaded from BridgePage because the only route from
   * BridgePage to this card is through SwapCard, which is Forbidden on this job — and reading it here
   * is the stronger guarantee anyway: BridgePage's `dexFee` memo never carries the flag, so it cannot
   * leak to `dexFeeUnavailable` (BridgePage.tsx:1788), which is the CTA gate (F-C7).
   *
   * HOISTED to a const (F-1, S1 security pass) because the `Bridge & swap cost` row below needs it
   * too, and a second `isCrossChainFeeDisplayEnabled()` call would be a second thing to keep in step.
   *
   * WHY THAT ROW TAKES A FLAG TERM AND THE MODAL'S DOES NOT. This row PRE-EXISTS F8 — the card has
   * carried it since #336 — so MED-4's `fixFeeSymbol` precondition on it changes what PRODUCTION
   * renders unless it is scoped to the flag. It was not, while four statements written by the same
   * diff promised flag-OFF was byte-identical to `2dc934e`: the falsified promote premise S1 filed as
   * F-1, on the exact claim the beta→prod promote decision rests on. Operator decision: GATE IT, so
   * flag-OFF is an unconditional no-op again and the mislabel fix ships with the flag like everything
   * else in F8. The modal's copy of this row is NEW in F8 and sits entirely behind `feeDisplayOn`, so
   * it cannot alter a flag-OFF render and needs no such term (ReviewTransferModal/index.tsx).
   */
  const feeDisplayOn = !!dexFee && isCrossChainFeeDisplayEnabled()
  /**
   * #261 F8 — the display-mode decision, taken by the ONE shared resolver the review modal also
   * calls, from the SAME payload every figure below is read out of (ADDENDUM C, C-1).
   */
  const dexDisplay = resolveDexFeeDisplay({
    feesTotalUsd: dexFee?.feesTotalUsd,
    fixFeeUsd: dexFee?.fixFeeUsd,
    // G4-1 — the SAME field the `Bridge & swap cost` row below is gated on (:333), on the branch where
    // that gate is live. The wedge excludes that fee, so it may only render when that row can. Passed
    // rather than re-checked at the row, so the two surfaces cannot drift apart on the same invariant.
    // The resolver needs no flag term of its own: it returns DISPLAY_OFF before this field is ever
    // read when the flag is off (dexTotal.ts), so the precondition is flag-ON-only by construction.
    fixFeeSymbol: dexFee?.fixFeeSymbol,
    youPaySource: dexFee?.youPaySource,
    crossChainFeeDisplayEnabled: feeDisplayOn,
  })
  // G2 (GATE-2 decision, unchanged by ADDENDUM C): the COLLAPSED label stays the ALL-IN. It is the
  // one figure visible without a tap, so it must be the total cost of the transaction; the split into
  // wedge + native flat fee is what the expanded panel is FOR.
  // #261 F8 / MED-7 — the clamp takes NO display parameter (dexTotal.ts). This call is therefore
  // identical in both flag states and identical to the CTA gate's own call (BridgePage.tsx:1792),
  // which is the structural half of F-C7.
  const dexTotals = dexFee ? computeDexDisplayTotals(dexFee) : null
  const dexTotalUsd = dexTotals ? dexTotals.usd : null
  const dexTotalTokenStr = dexTotals ? dexTotals.tokenStr : '0'

  // #336 R6 / DEV-362 — the ONE renderer for a DEX fee row's fiat figure. 'Fee unavailable' is
  // reserved for a figure that is GENUINELY absent (null/undefined/non-finite/negative) or for a
  // total the card has already refused to state; a real fee that merely rounds to zero at the 2-dp
  // (USD) render, OR at the render currency's OWN 2-dp precision, gets a true "< <bound>" statement
  // instead (DEV-362) — never the false "data is missing" implication, and never a hardcoded
  // '< $0.01': the bound is routed through `fiatFee` so it carries the user's selected currency
  // exactly like every other figure on this card.
  //
  // BRANCH ORDER IS THE WHOLE POINT — DO NOT REORDER:
  //   1. `usdIsMissing(usd)`                                     — genuinely missing. Checked FIRST
  //      and NEGATED (`!(usd != null && Number.isFinite(usd))` inside `usdIsMissing`) so it is
  //      fail-CLOSED on NaN.
  //      CORRECTED CLAIM (DEV-362 fixer pass, item 5): an earlier version of this comment said
  //      swapping clauses 1 and 4 alone would let a NaN reach `fiatFee(NaN)` and print "$NaN". That
  //      overstates it — `usdRendersAsZero` (clause 4) is ITSELF fail-closed on NaN
  //      (`!(NaN >= 0.005)` is `true`), so a bare reorder alone would still route NaN to the bound,
  //      producing a false "< $0.01" rather than "$NaN". Reaching "$NaN" needs the reorder AND a
  //      separate rewrite of the missing-check itself into a fail-OPEN (positive) form. Both failure
  //      modes are wrong — the point is only that clause order is not, on its own, what stands
  //      between this function and "$NaN"; the fail-closed shape of EVERY clause is.
  //   2. `!(n >= 0)`                                             — reject negative values. NEGATED so
  //      it is fail-CLOSED, and it happens to catch `NaN` and `-Infinity` too (`!(NaN >= 0)` and
  //      `!(-Infinity >= 0)` are both `true`). It does NOT catch `+Infinity`: `!(Infinity >= 0)` is
  //      `false`, so +Infinity passes straight through this clause. Do not read clause 2 as a general
  //      non-finite backstop — clause 1 is the ONLY guard covering the whole non-finite set, which is
  //      why a render-level `+Infinity` cell exists in the test file to bind that axis directly.
  //      The old code sent a negative straight to 'Fee unavailable'; without this clause a negative
  //      finite value would reach clause 4 and print "< $0.01" — a magnitude claim about garbage.
  //      Unreachable today (the emitter clamps every USD fee field non-negative) but an
  //      un-enumerated member of the input space is still a member of it.
  //   3. `dexTotals?.usd != null && dexTotals.belowDisplayFloor` — the TOTAL is already suppressed,
  //      NARROWED to the USD arm of `computeDexDisplayTotals` (`dexTotals.usd != null`). That arm is
  //      the ONLY one whose `belowDisplayFloor` is derived from `parseFeeUsd`/`networkFeeUsd`/
  //      `tronEnergyFeeUsd` — the same fields this row renders — so it is the only one this row's
  //      figure has any coherence claim against. The DEST-TOKEN arm (`dexTotals.usd === null`) sets
  //      `belowDisplayFloor` from a pre-formatted TOKEN STRING with zero dependence on any USD field;
  //      an above-floor USD row (e.g. a real `networkFeeUsd`) can coexist with that arm's
  //      `belowDisplayFloor === true`, and an unnarrowed clause here would deny a real charged fee on
  //      the screen before signing for no reason connected to the fee itself.
  //   4. `usdRendersAsZero(n)`                                   — a real, present, sub-floor USD
  //      figure by the shared USD threshold: the true "< bound" statement this ticket asks for.
  //   5. formatted result has no nonzero digit                  — the RENDER-level guard (DEV-362
  //      fixer pass, item 4). `usdRendersAsZero` is USD-denominated, but this row RENDERS in the
  //      user's selected fiat currency: for a low-value currency (GBP, rate ≈0.79) a `usd` that
  //      CLEARS the USD floor (e.g. 0.0055) can still convert to a figure that rounds to zero at that
  //      currency's own 2-dp Intl format ("≈ £0.00") — the exact false zero this whole file exists to
  //      prevent, reached through a door the USD-only threshold cannot see. Checked on the FORMATTED
  //      STRING, after conversion, which is the only place that fact is knowable.
  //      THE REGEX IS DELIBERATELY FAIL-CLOSED: `/[1-9]/` requires a Latin nonzero digit. A currency
  //      whose Intl formatting uses non-Latin digits (or a formatter this card has never seen) fails
  //      the test and falls through to the bound rather than to `≈ ${s}` — which can never print a
  //      false zero, only ever the true "smaller than this" statement. The failure mode of an
  //      unrecognised digit script is therefore "slightly over-cautious", never "prints a zero".
  //   6. otherwise                                               — unchanged: `≈ ${s}`.
  //
  // CRITICAL: this function must NEVER be the thing that changes `usdRendersAsZero`,
  // `belowDisplayFloor`, or `computeDexDisplayTotals` — those feed the SEND GATE
  // (`BridgePage.tsx` / `dexFeeUnavailable`) and must stay USD-based and untouched by a
  // render-currency concern. Clause 5 exists entirely at the RENDER layer, downstream of all three.
  //
  // Widened to accept `number | null | undefined`: the current three call sites are all `!= null`
  // (or stronger) gated at the JSX level, but clause 1 must be a REAL guard on the function's own
  // contract, not decorative — `NaN` is a `number` and slips past a TS type gate that only excludes
  // null/undefined.
  const fiatFeeRow = (usd: number | null | undefined): string => {
    if (usdIsMissing(usd)) return 'Fee unavailable'
    // `usdIsMissing` has already ruled out null/undefined/non-finite above, so `usd` is a real,
    // finite number from here on — safe to treat as `number`.
    const n = usd as number
    if (!(n >= 0)) return 'Fee unavailable'
    if (dexTotals?.usd != null && dexTotals.belowDisplayFloor) return 'Fee unavailable'
    if (usdRendersAsZero(n)) return `< ${fiatFee(USD_SUBCENT_BOUND)}`
    const s = fiatFee(n)
    if (!/[1-9]/.test(s)) return `< ${fiatFee(USD_SUBCENT_BOUND)}`
    return `≈ ${s}`
  }

  return (
    <div style={{ borderTop: `1px solid ${c.dv}`, marginTop: 20, paddingTop: 14 }}>
      {/* Toggle row */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: FONT_BODY, display: 'flex', alignItems: 'center',
            gap: 6, color: c.mu, fontSize: 13, fontWeight: 500, padding: 0,
          }}
        >
          <Info size={13} />
          {hasFees
            ? (dexFee
              ? (dexTotals?.belowDisplayFloor
                ? 'Fee unavailable'
                : dexTotalUsd != null
                ? `≈ ${fiatFee(dexTotalUsd)} total fees`
                : `${dexTotalTokenStr} ${dexFee.currency} total fee`)
              : feeEstimate && (fiatRate != null
                ? <>{displayTotalFee} USDC{fiatNote(displayTotalFee)} total fee</>
                : `${fiatSymbol}${displayTotalFee} total fee`))
            : dexFeeRefused
              ? 'Fee unavailable'   // #336 R10 — same string as every other suppressed figure here.
              : 'Fee details'}
          <ChevronDown
            size={12}
            style={{
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: '0.2s',
            }}
          />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Fee-warning badge — 'Elevated' and 'High' are DELIBERATELY not shown (product decision
              2026-07-18: clutter that panics users). The tiers are bps-based, but a bridge's third-party
              cost is largely FIXED dollars (deBridge opex/fixFee ~$0.05-0.08), so on a small notional they
              always trip: $0.08 is ~457 bps on a $1.75 swap but 8 bps on $100. Every small swap read
              "Elevated", which is noise, not signal — and a permanent warning trains users to ignore
              warnings. The exact fee is always visible in the panel below, so nothing is hidden.

              '>100%' IS still shown. That is not a proportional-cost warning — it means the fees exceed
              the amount being sent, i.e. the user would receive ~nothing. Suppressing that would let
              someone knowingly-unknowingly burn their money, which is a different class of thing from a
              noisy badge. */}
          {feeWarning?.level === 'exceeds_amount' && hasFees && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 3,
              fontSize: 12, fontWeight: 600, padding: '3px 8px',
              borderRadius: 8,
              background: c.eB,
              color: c.eC,
            }}>
              <AlertTriangle size={12} />
              &gt;100%
            </div>
          )}
          {amountNum > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: c.gr, fontWeight: 500 }}>
              <Clock size={13} />{deliveryTime}
            </div>
          )}
          {/* #261 L5 — the quote/balance refresh countdown, LABELLED.
              It used to render as a bare "25s" immediately after the Clock ETA, so "1–3 min  25s"
              read as one figure — i.e. as part of how long the user's money would take to arrive.
              It is not: it is BridgePage's 30s re-quote timer. The label is what
              separates them; the dot is only visual, so it is hidden from assistive tech.
              PRESENTATION ONLY — this component neither owns the timer nor triggers the refresh,
              it renders the prop, so nothing here changes the refresh cadence or its cost. */}
          {hasFees && (
            <>
              {amountNum > 0 && <span aria-hidden="true" style={{ fontSize: 11, color: c.ts }}>·</span>}
              <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 11, color: c.ts }}>
                Refreshes in {secondsRemaining}s
              </span>
            </>
          )}
        </div>
      </div>

      {/* Gas-spike "Xx higher than usual" warning removed 2026-07: redundant with the shown fee + net
          received amount, and alarming on trivial fees (Solana's near-zero base reads as "123x"). The
          backend still emits gasConditions (harmless/unused); restore this block + the `gasConditions`
          const from git if a signal is ever wanted. */}

      {/* Expanded breakdown */}
      {expanded && hasFees && (
        <div style={{
          marginTop: 10, padding: 14, background: c.iB,
          borderRadius: 12, fontSize: 13, color: c.ts, fontWeight: 500,
          fontFamily: FONT_BODY,
          animation: 'fadeIn 0.15s ease-out',
        }}>
          {dexFee ? (
            // ── DEX/swap route ──
            // Two fee surfaces: the deBridge flat protocol fee is paid in the SOURCE token on top of
            // the input (the "you'll pay" surprise); the Parse platform fee + route margin come out of
            // the DEST value. "Total fees" is the all-in USD (you'll-pay − receive). "You'll pay" mirrors
            // the wallet's source-token debit (excl. network gas, which the wallet adds).
            <>
              {/* The rail's flat protocol fee (e.g. deBridge globalFixedNativeFee), sent as the tx VALUE in
                  the source chain's native token — NOT gas. PURE PASS-THROUGH: it's exactly the provider's
                  protocol cost (no Parse margin — Parse's cut is the separate "Parse fee" line below). Labeled
                  provider-agnostically as "Bridge & swap cost" (mirrors the CTA, doesn't leak the vendor). */}
              {/* #261 F8 / MED-4 — GATED ON THE SYMBOL, not just the amount, WHEN THE FLAG IS ON.
                  `fixFeeNative` is published unconditionally while `fixFeeSymbol` is
                  `srcNativeRow?.symbol ?? null` (dex.ts:1217), so a failed source-native lookup falls
                  back to `dexFee.sourceCurrency` and prints the SOLANA fee as "0.015 USDC" — a native
                  amount under the source token's ticker, which is the I-5 shape (a figure rendered in
                  a denomination it is not in) on a fee the user is actually charged. There is no
                  honest label available in that state, so the row is withheld rather than mislabelled.
                  F-1 (S1) / F-D — THE `feeDisplayOn` TERM IS LOAD-BEARING, NOT DECORATION. This row
                  is older than F8, so an unconditional symbol precondition would change PRODUCTION
                  rendering while the flag is off — which is precisely what four statements in this
                  same change promise it does not (flags.ts, vite-env.d.ts, deploy-web.yml,
                  deployAllowlist.test.ts), and what the promote decision leans on. Flag OFF the gate
                  and the `?? sourceCurrency` fallback below are EXACTLY `2dc934e`; the mislabel is
                  fixed when the flag flips, with everything else in F8. Production is not exposed
                  today either way: all nine DEX chains carry a seeded native row, so the payload is
                  unreachable on the verified prod DB — the fix ships on the flag because the promise
                  of an unconditional flag-OFF no-op is worth more than closing an unreachable state
                  early.
                  G4-1 — WITHHOLDING IS ONLY SAFE BECAUSE THE RESOLVER REFUSES THE WEDGE ON THE SAME
                  CONDITION. This comment used to end "the fee remains inside the all-in `Total fees`
                  figure either way", which was FALSE on the wedge branch: the wedge EXCLUDES this fee
                  by construction, so withholding its only line put a charged $1.14 nowhere on the
                  screen. `resolveDexFeeDisplay` takes `fixFeeSymbol` and falls back to the all-in when
                  it is absent (dexTotal.ts), which makes the claim true again — it is a coupling, not
                  a coincidence. The resolver carries no `feeDisplayOn` term because it is already
                  inside the flag-ON region (it returns DISPLAY_OFF first), so the two conditions still
                  agree exactly where both are live. Do not gate this row on a condition the resolver
                  does not see, and do not "align" the two by deleting the flag term here. */}
              {dexFee.fixFeeNative != null && (!feeDisplayOn || dexFee.fixFeeSymbol != null) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Bridge &amp; swap cost</span>
                  <span style={{ color: c.tx, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {/* `?? dexFee.sourceCurrency` is `2dc934e` verbatim and is the FLAG-OFF arm: with
                        the flag ON the gate above has already established `fixFeeSymbol != null`, so
                        the fallback is unreachable there and no mislabel can render. */}
                    {trimAmt(dexFee.fixFeeNative)} {dexFee.fixFeeSymbol ?? dexFee.sourceCurrency}
                    {/* #336 R6 — this one is a PARENTHETICAL beside a truthful native amount, not the
                        row's primary figure, so the treatment is full SUPPRESSION (the parenthetical is
                        dropped entirely on a sub-cent value) rather than a worded string.
                        CORRECTED (DEV-362 fixer pass, item 8): this comment used to contrast that
                        suppression against "the 'Fee unavailable' string the primary rows use" — that
                        contrast no longer holds. Since DEV-362 the primary rows (`fiatFeeRow`) print a
                        true "< $0.01" on this same sub-cent condition, not 'Fee unavailable'; only a
                        TOTAL the card has already refused to state still says that. The actual reason
                        this parenthetical is dropped rather than worded is different: it sits beside a
                        truthful, ALREADY-VISIBLE native amount in the same row ("0.0001 ETH"), so a
                        "< $0.01" annotation would be a second, redundant magnitude claim about a figure
                        the reader can already see — dropping it loses nothing the row doesn't already
                        state. Same threshold (`usdRendersAsZero`), and the same precedent as the CCTP
                        native-fee parenthetical below (:397-398), which is likewise gated rather than
                        worded. What is banned either way is "(≈ $0.00)" on a fee the user is actually
                        charged. */}
                    {/* #261 F8 / E8 — the parenthetical is UNCONDITIONAL again (C-4/E6's suppression
                        is RETIRED). C-4 suppressed it because "$1.14" beside a row named `Total fees
                        ≈ $1.01` is a line item larger than its own total — issue #50's defect.
                        STALE COPY REPAIRED (GATE 4, DEV-363 FIX-B — copy 8 of the F8 contract): this
                        comment used to describe E8 as renaming that row to `Fees from your receive`
                        and the two figures as visibly summing to the all-in. DEV-363 ITEM A retired
                        that row on this branch: on the wedge case the fee-total row is WITHHELD
                        entirely rather than renamed (see SPACE 1 in the committed SURFACE file), so
                        there is no row on screen for this parenthetical to be "larger than", and
                        nothing here sums to anything. The conclusion E8 reached still holds, on a
                        stronger premise: the contradiction C-4 suppressed for cannot arise at all now
                        that the row it contradicted is gone, so suppressing this parenthetical would
                        cost the SOL amount its only magnitude anchor for no offsetting reason. */}
                    {dexFee.fixFeeUsd != null && !usdRendersAsZero(dexFee.fixFeeUsd) && (
                      <span style={{ color: c.mu, marginLeft: 4 }}>({fiatFee(dexFee.fixFeeUsd)})</span>
                    )}
                    {/* #261 F8 / E8 — on the wedge branch the fee-total row EXCLUDES this fee, so the
                        row says so out loud rather than leaving the reader to infer it. F-2: the line
                        is never deleted and never presented as if it were inside a total it is not
                        in. Scoped to `paidSeparately`, i.e. exactly where the wedge renders — on the
                        C-2 fallback the row is the all-in again and this fee IS inside it. */}
                    {dexDisplay.paidSeparately && (
                      <span style={{ color: c.mu, fontWeight: 500, display: 'block', fontSize: 11 }}>
                        — paid separately in {dexFee.fixFeeSymbol}
                      </span>
                    )}
                    {/* #261 F8 / C-8 (operator E7) — on a SINGLE-TOKEN-DEBIT source the flat fee is
                        NOT double-counted: it is already inside `youPayUsd`, hence inside
                        `feesTotalUsd` (dex.ts:1256-1258), so the breakdown here already reconciles
                        (base ETH→RH: $1.88 + $0.53 = $2.41 exactly). Deleting the line would leave
                        that user a total with 78% of it unitemised; F-2 bans that. It is RELABELLED
                        instead, so the row cannot be read as a cost sitting outside the total. */}
                    {dexDisplay.includedInYouPay && (
                      <span style={{ color: c.mu, fontWeight: 500, display: 'block', fontSize: 11 }}>
                        — included in You&apos;ll pay
                      </span>
                    )}
                  </span>
                </div>
              )}
              {/* Generic provider/rail cost (e.g. the 1Click rail). PURE PASS-THROUGH (the provider's rail
                  cost, no Parse margin — Parse's cut is the separate "Parse fee" line). Same provider-agnostic
                  "Bridge & swap cost" label as the deBridge fixFee above; the two are mutually exclusive per
                  provider (deBridge → fixFeeNative; 1Click → networkFeeUsd), so only one ever shows. */}
              {dexFee.networkFeeUsd != null && dexFee.networkFeeUsd > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Bridge &amp; swap cost</span>
                  <span style={{ color: c.tx, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {/* #336 R6 — the `> 0` gate on this row does NOT stop a sub-cent value: 0.003 is
                        > 0 and renders "≈ $0.00". 4-dp at source (dex.ts:857). */}
                    {fiatFeeRow(dexFee.networkFeeUsd)}
                  </span>
                </div>
              )}
              {/* Tron source-side energy burn (TRX spent to energize the TRC-20 deposit transfer). Shown
                  separately so the user sees the real source cost; flagged when the wallet has no energy. */}
              {dexFee.tronEnergyFeeUsd != null && dexFee.tronEnergyFeeUsd > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>
                    Tron energy
                    {dexFee.tronHasEnergy === false && (
                      <span style={{ color: c.mu, marginLeft: 4, fontSize: 11 }}>(no energy — TRX burned)</span>
                    )}
                  </span>
                  <span style={{ color: c.tx, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {/* #336 R6 — 2-dp at source (dex.ts:860), so this can only ever be an exact 0
                        here; routed through the shared guard anyway so the rule holds by
                        construction rather than by the emitter's current precision. */}
                    {fiatFeeRow(dexFee.tronEnergyFeeUsd)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Parse DeFi fee</span>
                <span style={{ color: c.tx, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                  {/* #336 R1 — the dest-token arm is gated on the SAME `belowDisplayFloor` as the
                      Total row below. Ungated it printed "Parse DeFi fee 0 ETH" directly ABOVE
                      "Total fees Fee unavailable": the exact false zero this card exists to remove,
                      one row up, on the same screen. The token arm is precisely where that bites —
                      in dest-token mode `parseFeeUsd` is structurally absent (dex.ts:857 emits it
                      only alongside `feesTotalUsd` at :863), so the token arm ALWAYS fires there. */}
                  {/* #336 R6 — the USD arm is FIRST and used to be unconditional, so R1's gate on the
                      token arm below never saw a sub-cent `parseFeeUsd`: 0.003 printed "≈ $0.00"
                      above "Total fees Fee unavailable". `fiatFeeRow` closes that arm with the SAME
                      THRESHOLD as the total's own suppression check.
                      CORRECTED (DEV-362 fixer pass, item 7): this used to also claim "and the same
                      string as the total" — that is now false. Since DEV-362 gave sub-cent fees their
                      own truthful "< $0.01" wording, this row and the total row can legitimately
                      disagree in STRING while agreeing in THRESHOLD: a sub-cent `parseFeeUsd` beside a
                      truthful, above-floor total prints "< $0.01" here against "≈ $1.50" there (see the
                      R6 test suite in index.test.tsx). They still share a string ('Fee unavailable') on
                      exactly the coherence-clause case — when the TOTAL ITSELF is sub-floor — which is
                      the narrower, correct claim. */}
                  {dexFee.parseFeeUsd != null
                    ? fiatFeeRow(dexFee.parseFeeUsd)
                    : dexTotals?.belowDisplayFloor
                    ? 'Fee unavailable'
                    : `${dexFee.parseFee} ${dexFee.currency}`}
                </span>
              </div>
              {/* DEV-363 ITEM A — THE FEE-TOTAL ROW IS WITHHELD ON THE STABLE-SPLIT WEDGE BRANCH.
                  Operator decision (Jira DEV-363, GATE 1 2026-09-07): the row E8 renamed
                  `Fees from your receive` is REMOVED rather than renamed again. The gate is the same
                  discriminant the label and the figure used to branch on — `dexDisplay.wedgeUsd` — so
                  the removal lands on exactly the one resolver exit that can set it
                  (`dexTotal.ts:361-367`, the stableSplit WEDGE sub-branch) and on no other.

                  THE TWO ARMS THAT USED TO LIVE HERE ARE GONE, NOT LEFT DEAD. Inside this block
                  `wedgeUsd` is provably null, so a `wedgeUsd != null ? 'Fees from your receive'` arm
                  could never render — dead code that looks wired. The label is the literal
                  `Total fees`; the value keeps the remaining chain verbatim
                  (`belowDisplayFloor` → `Fee unavailable`, then the USD all-in, then the token string).

                  FLAG-OFF IS UNTOUCHED, STRUCTURALLY. `resolveDexFeeDisplay` returns DISPLAY_OFF on its
                  first line when the flag is off (`dexTotal.ts:346`), and DISPLAY_OFF carries
                  `wedgeUsd: null` — so this gate is unconditionally TRUE with F8 OFF and production
                  renders byte-identically. Same for singleTokenDebit, the C-2/G4-1 stableSplit fallback
                  and legacy: all four also carry `wedgeUsd: null` and keep their row.

                  ACCEPTED TRADE-OFF, recorded in the ticket, NOT a defect to file: the COLLAPSED
                  summary at :315 still prints the all-in `dexTotalUsd` and is deliberately NOT gated,
                  so on the wedge branch the expanded panel no longer sums to the collapsed figure. */}
              {dexDisplay.wedgeUsd == null && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  borderTop: `1px solid ${c.dv}`, paddingTop: 8,
                  fontWeight: 600, color: c.tx,
                }}>
                  <span>Total fees</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {dexTotals?.belowDisplayFloor
                      ? 'Fee unavailable'
                      : dexTotalUsd != null
                      ? `≈ ${fiatFee(dexTotalUsd)}`
                      : `${dexTotalTokenStr} ${dexFee.currency}`}
                  </span>
                </div>
              )}
              {dexFee.youPaySource != null && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  borderTop: `1px solid ${c.dv}`, marginTop: 8, paddingTop: 8,
                  fontWeight: 600, color: c.tx,
                }}>
                  <span>You'll pay</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                    ≈ {trimAmt(dexFee.youPaySource)} {dexFee.sourceCurrency}
                    <span style={{ color: c.mu, fontWeight: 500, display: 'block', fontSize: 11 }}>
                      + network gas
                    </span>
                  </span>
                </div>
              )}
              {dexFee.minimumReceived && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: c.mu }}>
                  <span>Minimum received</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{dexFee.minimumReceived} {dexFee.currency}</span>
                </div>
              )}
            </>
          ) : feeEstimate ? (
            // ── CCTP route ──
            <>
              {/* Provider-agnostic rail cost — our relayer gas + Circle's fast-transfer pass-through folded
                  into ONE "Bridge & swap cost" line (same label the DEX branch uses; matches the
                  pre-itemization UX). Parse's cut stays the separate "Parse DeFi fee" line below. The folded
                  number still makes the breakdown sum exactly to PAY − RECEIVE. Circle's maxFee is absent on
                  Solana-source (program supplies its own), so this is just networkFeeUsdc there. */}
              {(() => {
                const railUsdc = trimAmt(
                  (parseFloat(feeEstimate.networkFeeUsdc || '0') +
                    (feeEstimate.circleMaxFeeUsdc ? parseFloat(feeEstimate.circleMaxFeeUsdc) : 0)).toFixed(6),
                );
                return (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Bridge &amp; swap cost</span>
                    <span style={{ color: c.tx, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                      {railUsdc} USDC{fiatNote(railUsdc)}
                      {/* Native-fee parenthetical: render ONLY when it's a real non-zero value. The API-level
                          "native fee display" is currently disabled and emits the literal string '0' (truthy),
                          so a bare truthiness check rendered a meaningless "(0)". Gate on parsed value > 0. */}
                      {parseFloat(feeEstimate.networkFeeNativeDisplay || '0') > 0 && (
                        <span style={{ color: c.mu, marginLeft: 4 }}>({feeEstimate.networkFeeNativeDisplay})</span>
                      )}
                    </span>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Parse DeFi fee</span>
                <span style={{ color: c.tx, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                  {feeEstimate.baseFee} USDC{fiatNote(feeEstimate.baseFee)}
                </span>
              </div>
              {feeEstimate.referrer && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12, color: c.mu }}>
                  <span style={{ paddingLeft: 12 }}>
                    Referrer ({(feeEstimate.referrer.shareBps / 100).toFixed(0)}%)
                  </span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {feeEstimate.referrer.feeAmount} USDC
                  </span>
                </div>
              )}
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                borderTop: `1px solid ${c.dv}`, paddingTop: 8,
                fontWeight: 600, color: c.tx,
              }}>
                <span>Total</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fiatRate != null
                    ? <>{displayTotalFee} USDC{fiatNote(displayTotalFee)}</>
                    : <>{fiatSymbol}{displayTotalFee}</>
                  }
                </span>
              </div>
              {(minReceivedAmount || feeEstimate.minimumReceived) && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  marginTop: 6, fontSize: 12, color: c.mu,
                }}>
                  <span>Minimum received</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {minReceivedAmount
                      ? <>{minReceivedAmount} {minReceivedSymbol ?? 'USDC'}</>
                      : <>{feeEstimate.minimumReceived} USDC{fiatNote(feeEstimate.minimumReceived)}</>}
                  </span>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}
